import { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

export default function AuthModal({ open, onClose }) {
  const [mode, setMode] = useState('login');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    email_notifications: false,
    privacy_accepted: false,
    terms_accepted: false
  });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
      toast.success('Login effettuato con successo!');
      } else {
        await register(formData);
        toast.success('Registrazione completata!');
      }
      onClose();
      setFormData({ name: '', email: '', password: '', phone: '', address: '', email_notifications: false, privacy_accepted: false, terms_accepted: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'autenticazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            {mode === 'login' ? 'Accedi' : 'Registrati'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {mode === 'register' && (
            <div>
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="name-input"
              />
            </div>
          )}

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="email-input"
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              data-testid="password-input"
            />
          </div>

          {mode === 'register' && (
            <>
              <div>
                <Label htmlFor="phone">Telefono (opzionale)</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="phone-input"
                />
              </div>

              <div>
                <Label htmlFor="address">Indirizzo (opzionale)</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  data-testid="address-input"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="notifications"
                  checked={formData.email_notifications}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, email_notifications: checked })
                  }
                  data-testid="notifications-checkbox"
                />
                <Label htmlFor="notifications" className="text-sm">
                  Ricevi notifiche via email
                </Label>
              </div>

              <div className="space-y-3 pt-2 border-t">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="privacy"
                    checked={formData.privacy_accepted}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, privacy_accepted: checked })
                    }
                    required
                    data-testid="privacy-checkbox"
                  />
                  <Label htmlFor="privacy" className="text-sm leading-tight cursor-pointer">
                    Accetto la{' '}
                    <a href="/privacy-policy" target="_blank" className="text-rose-600 hover:underline font-semibold">
                      Privacy Policy
                    </a>{' '}
                    e il trattamento dei miei dati personali *
                  </Label>
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={formData.terms_accepted}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, terms_accepted: checked })
                    }
                    required
                    data-testid="terms-checkbox"
                  />
                  <Label htmlFor="terms" className="text-sm leading-tight cursor-pointer">
                    Accetto i{' '}
                    <a href="/terms-of-service" target="_blank" className="text-rose-600 hover:underline font-semibold">
                      Termini di Servizio
                    </a>{' '}
                    *
                  </Label>
                </div>

                <p className="text-xs text-gray-500">* Campi obbligatori</p>
              </div>
            </>
          )}

          <Button
            type="submit"
            disabled={loading || (mode === 'register' && (!formData.privacy_accepted || !formData.terms_accepted))}
            className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-full font-bold py-6 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-button"
          >
            {loading ? 'Attendere...' : mode === 'login' ? 'Accedi' : 'Registrati'}
          </Button>

          <div className="text-center text-sm">
            {mode === 'login' ? (
              <>
                Non hai un account?{' '}
                <button
                  type="button"
                  onClick={() => setMode('register')}
                  className="text-rose-600 font-semibold hover:underline"
                  data-testid="switch-to-register"
                >
                  Registrati
                </button>
              </>
            ) : (
              <>
                Hai già un account?{' '}
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="text-rose-600 font-semibold hover:underline"
                  data-testid="switch-to-login"
                >
                  Accedi
                </button>
              </>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}